import { useEffect } from "react";
import { createPortal } from "react-dom";
import { ArrowUpRight, Phone } from "lucide-react";

/**
 * Booking primitives, shared by the ventures grid and the venture pages.
 *
 * Rules held here:
 *  - A rate is display type, not a label. If a venue publishes a number, that
 *    number is the loudest thing in the block.
 *  - Every phone number is a tel: link. A number rendered as text on a phone
 *    is a number nobody rings.
 *  - Outbound links carry rel="noopener" and a utm so an originated booking
 *    is attributable back to this site.
 *  - Nothing renders if the data is absent. No empty states, no "coming soon".
 */

const withUtm = (href) => {
  try {
    const u = new URL(href);
    u.searchParams.set("utm_source", "shruthiravi");
    u.searchParams.set("utm_medium", "portfolio");
    return u.toString();
  } catch {
    return href;
  }
};

/** Published rates, as a ruled ledger. */
export const RateCard = ({ rates, tone = "dark", className = "" }) => {
  if (!rates || rates.length === 0) return null;
  const rule = tone === "dark" ? "border-cream/15" : "border-ink/15";
  const muted = tone === "dark" ? "text-cream/60" : "text-ink2";
  const strong = tone === "dark" ? "text-cream" : "text-ink";

  return (
    <dl
      /* Stacks below sm. The narrowest three up column is 95.8px at vw 768
         and "Rs 65,000" measures 106px at 30px, so the columns crushed the
         numbers before this breakpoint existed. */
      className={`grid grid-cols-1 gap-y-6 sm:grid-cols-3 sm:gap-y-0 sm:gap-x-px ${className}`}
      data-testid="rate-card"
    >
      {rates.map((r) => (
        <div key={r.name} className={`border-t ${rule} pt-4`}>
          <dt
            className={`label ${muted}`}
          >
            {r.name}
          </dt>
          <dd
            className={`mt-2 font-display text-3xl lg:text-4xl leading-none tracking-tight whitespace-nowrap ${strong}`}
          >
            {r.price}
          </dd>
        </div>
      ))}
    </dl>
  );
};

/**
 * The action block. `primary` is the venue's own booking surface, `phone` is the
 * faster path on a handset, and both are always real.
 */
export const BookingActions = ({
  venture,
  tone = "dark",
  size = "md",
  className = "",
}) => {
  const { booking, contact = {} } = venture;
  if (!booking && !contact.phone) return null;

  const pad = size === "lg" ? "px-7 py-4" : "px-6 py-3.5";
  const primaryTone =
    tone === "dark"
      ? "bg-cream text-ink hover:bg-gold"
      : "bg-ink text-cream hover:bg-terracotta";
  const ghostTone =
    tone === "dark"
      ? "border-cream/40 text-cream hover:bg-cream hover:text-ink"
      : "border-ink/40 text-ink hover:bg-ink hover:text-cream";

  return (
    <div className={`flex flex-wrap items-center gap-3 ${className}`}>
      {booking && (
        <a
          href={withUtm(booking.href)}
          target="_blank"
          rel="noopener"
          data-cursor="hover"
          data-testid={`book-${venture.slug}`}
          className={`group inline-flex items-center gap-3 ${pad} ${primaryTone} label transition-colors duration-500`}
        >
          {booking.label}
          <ArrowUpRight
            size={14}
            className="transition-transform duration-300 group-hover:rotate-45"
          />
        </a>
      )}

      {contact.phone && (
        <a
          href={`tel:${contact.phone}`}
          data-cursor="hover"
          data-testid={`call-${venture.slug}`}
          className={`inline-flex items-center gap-2.5 ${pad} border ${ghostTone} label transition-colors duration-500`}
        >
          <Phone size={13} />
          {contact.phoneLabel || "Call"}
        </a>
      )}
    </div>
  );
};

/** Hours, address and the rate basis. Only the fields that exist. */
export const BookingMeta = ({ venture, tone = "dark", className = "" }) => {
  const { contact = {}, booking } = venture;
  const muted = tone === "dark" ? "text-cream/60" : "text-ink2";
  const bits = [contact.hours, booking && booking.note, contact.address].filter(
    Boolean
  );
  if (bits.length === 0) return null;

  return (
    <p
      className={`label ${muted} ${className}`}
      data-testid={`booking-meta-${venture.slug}`}
    >
      {bits.join(" · ")}
    </p>
  );
};

/**
 * Mobile sticky book bar for a venture page.
 *
 * Portalled to <body>: the page wrapper carries `overflow-x: clip`, which clips
 * fixed descendants, so rendering in place would hide the bar. Also sets
 * data-bookbar on <body> so index.css can lift the chat trigger out of the way
 * instead of the two overlapping.
 */
export const StickyBookBar = ({ venture }) => {
  const hasAction = Boolean(venture.booking || (venture.contact || {}).phone);

  useEffect(() => {
    if (!hasAction) return;
    document.body.setAttribute("data-bookbar", "true");
    return () => document.body.removeAttribute("data-bookbar");
  }, [hasAction]);

  if (!hasAction) return null;
  const { booking, contact = {} } = venture;

  return createPortal(
    <div
      className="lg:hidden fixed bottom-0 left-0 right-0 z-[46] bg-ink/95 backdrop-blur-md border-t border-cream/15"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      data-testid={`sticky-book-${venture.slug}`}
    >
      <div className="flex items-stretch">
        {contact.phone && (
          <a
            href={`tel:${contact.phone}`}
            className="flex items-center justify-center gap-2 px-5 py-4 text-cream border-r border-cream/15 label"
            data-testid={`sticky-call-${venture.slug}`}
          >
            <Phone size={14} />
            Call
          </a>
        )}
        {booking && (
          <a
            href={withUtm(booking.href)}
            target="_blank"
            rel="noopener"
            className="flex-1 flex items-center justify-center gap-2 px-5 py-4 bg-gold text-ink label"
            data-testid={`sticky-book-cta-${venture.slug}`}
          >
            {booking.label}
            <ArrowUpRight size={14} />
          </a>
        )}
      </div>
    </div>,
    document.body
  );
};
